import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LayoutDashboard, LogOut, Mail, ShieldCheck, UserCog } from 'lucide-react';
import { BRAND, ROUTES } from '@/lib/constants';
import { HEADER } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';

export const Header = ({ transparent = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isOnDarkHero = transparent && !scrolled;

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.home);
  };

  const initials =
    (user?.first_name?.[0] || '') + (user?.last_name?.[0] || '') || 'NS';

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[var(--ns-bg)]/92 backdrop-blur-md border-b border-[var(--ns-border)] shadow-[0_4px_24px_rgba(28,22,18,0.06)]'
          : transparent
          ? 'bg-transparent border-b border-transparent'
          : 'bg-[var(--ns-bg)] border-b border-[var(--ns-border)]'
      }`}
      data-testid={HEADER.nav}
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
        <Link
          to={ROUTES.home}
          className="flex items-center group"
          data-testid={HEADER.brand}
          aria-label="NetScouters - Inicio"
        >
          {isOnDarkHero ? (
            <span className="font-display uppercase tracking-[0.12em] sm:tracking-[0.18em] text-white text-base sm:text-xl md:text-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">
              NetScouters
            </span>
          ) : (
            <img
              src={BRAND.logoUrl}
              alt="NetScouters"
              className="h-6 sm:h-8 w-auto select-none transition-opacity duration-200 group-hover:opacity-90"
              draggable={false}
            />
          )}
        </Link>

        <nav className="flex items-center gap-0.5 sm:gap-2">
          <NavLink
            to={ROUTES.clubs}
            className={({ isActive }) =>
              `px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? isOnDarkHero
                    ? 'text-[var(--ns-gold)]'
                    : 'text-[var(--ns-gold-600)]'
                  : isOnDarkHero
                  ? 'text-white hover:bg-white/15'
                  : 'text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]'
              }`
            }
            data-testid={HEADER.clubsLink}
          >
            Clubes
          </NavLink>
          <NavLink
            to={ROUTES.events}
            className={({ isActive }) =>
              `px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? isOnDarkHero
                    ? 'text-[var(--ns-gold)]'
                    : 'text-[var(--ns-gold-600)]'
                  : isOnDarkHero
                  ? 'text-white hover:bg-white/15'
                  : 'text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]'
              }`
            }
            data-testid={HEADER.eventsLink}
          >
            Partidos
          </NavLink>

          {/* Soporte · pastilla discreta siempre visible, con icono de sobre
              y mailto. Reutiliza tokens del header para respetar hero oscuro
              y vistas claras sin rediseñar la barra. */}
          <a
            href="mailto:soporte@netscouters.tv"
            data-testid="header-support-email"
            className={`hidden md:inline-flex items-center gap-2 h-9 px-3 ml-1 sm:ml-2 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
              isOnDarkHero
                ? 'border-white/25 text-white hover:bg-white/15'
                : 'border-[var(--ns-border)] text-[var(--ns-text)] hover:border-[var(--ns-gold)]/40 hover:bg-[var(--ns-bg-2)]'
            }`}
            title="Contacta con soporte"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            <span>soporte@netscouters.tv</span>
          </a>

          {!isAuthenticated && (
            <Button
              asChild
              className="ml-1 sm:ml-2 h-8 sm:h-9 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-sm font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] transition-all duration-200"
              data-testid={HEADER.loginLink}
            >
              <Link
                to={ROUTES.login}
                state={{ from: location.pathname + location.search }}
              >
                Acceder
              </Link>
            </Button>
          )}

          {isAuthenticated && (
            <>
              <NotificationsBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={HEADER.userMenuTrigger}
                  className="ml-1 sm:ml-2 inline-flex items-center gap-2 h-9 pl-1 pr-3 rounded-full bg-white border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 transition-colors"
                  aria-label="Mi cuenta"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-gradient-to-br from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-bold">
                      {initials.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-[var(--ns-text)] max-w-[90px] truncate hidden sm:inline">
                    {user?.first_name || 'Mi cuenta'}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-[var(--ns-text-muted)] truncate">
                  {user?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid={HEADER.userMenuDashboard}
                  onClick={() => navigate(ROUTES.dashboard)}
                  className="cursor-pointer"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2 text-[var(--ns-gold-600)]" />
                  Mi dashboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={HEADER.userMenuAccount}
                  onClick={() => navigate(ROUTES.account)}
                  className="cursor-pointer"
                >
                  <UserCog className="h-4 w-4 mr-2 text-[var(--ns-gold-600)]" />
                  Mi cuenta
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    data-testid={HEADER.userMenuAdmin}
                    onClick={() => navigate(ROUTES.admin)}
                    className="cursor-pointer"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2 text-[var(--ns-gold-600)]" />
                    Administración
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid={HEADER.userMenuLogout}
                  onClick={handleLogout}
                  className="cursor-pointer text-[var(--ns-red)] focus:text-[var(--ns-red)]"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
